import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Alert,
  ActivityIndicator,
  AppState,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import * as DocumentPicker from 'expo-document-picker';
import { storage } from '../../utils/storage';
import { AppLayout } from '../../components/layout/AppLayout';
import { supabase } from '../../api/supabase';
import theme from '../../theme';

interface ImportProgress {
  phase: 'idle' | 'uploading' | 'processing' | 'complete' | 'error';
  currentStep: number;
  totalSteps: number;
  message: string;
  details?: string;
  processed?: number;
  total?: number;
  sessionId?: string;
}

interface ImportStats {
  totalRecords: number;
  vehicleMatches: number;
  plateMatches: number;
  notFound: number;
  totalAmount: number;
  accuracy: number;
  duplicatesRemoved: number;
  dateRange?: string;
  batchId?: string;
  sourceFile?: string;
  importType?: string;
  fileHash?: string;
  processedRecords?: number;
  rawRecordsSaved?: number;
  categorizedFines?: {
    parking: number;
    speeding: number;
    traffic: number;
    other: number;
  };
}

export const FineImportScreenFixed = () => {
  const navigation = useNavigation();
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [progress, setProgress] = useState<ImportProgress>({
    phase: 'idle',
    currentStep: 0,
    totalSteps: 6,
    message: 'Ready to import traffic fines data',
  });
  const [importStats, setImportStats] = useState<ImportStats | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const appState = useRef(AppState.currentState);

  // Generate unique session ID for this import session
  useEffect(() => {
    const newSessionId = `fines_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    loadSessionState(newSessionId);
  }, []);

  // Handle app state changes (background/foreground)
  useEffect(() => {
    const subscription = AppState.addEventListener('change', handleAppStateChange);
    return () => subscription?.remove();
  }, []);

  const handleAppStateChange = (nextAppState: string) => {
    if (appState.current.match(/inactive|background/) && nextAppState === 'active') {
      if (isProcessing) {
        restoreProcessingState();
      }
    } else if (nextAppState.match(/inactive|background/) && isProcessing) {
      saveSessionState();
    }
    appState.current = nextAppState;
  };

  const saveSessionState = async () => {
    try {
      const state = {
        selectedFile,
        progress,
        importStats,
        isProcessing,
        sessionId,
        timestamp: Date.now()
      };
      await storage.setItem(`fines_import_${sessionId}`, JSON.stringify(state));
    } catch (error) {
      console.warn('Failed to save session state:', error);
    }
  };

  const loadSessionState = async (sid: string) => {
    try {
      const savedState = await storage.getItem(`fines_import_${sid}`);
      if (savedState) {
        const state = JSON.parse(savedState);
        if (Date.now() - state.timestamp < 30 * 60 * 1000) {
          setSelectedFile(state.selectedFile);
          setProgress(state.progress);
          setImportStats(state.importStats);
          setIsProcessing(state.isProcessing);
        } else {
          await storage.removeItem(`fines_import_${sid}`);
        }
      }
    } catch (error) {
      console.warn('Failed to load session state:', error);
    }
  };

  const restoreProcessingState = async () => {
    try {
      if (progress.sessionId) {
        const { data: batchStatus } = await supabase
          .from('accounting_transactions')
          .select('id, created_at')
          .eq('category', 'fines')
          .eq('import_batch_id', progress.sessionId)
          .order('created_at', { ascending: false })
          .limit(1);

        if (batchStatus && batchStatus.length > 0) {
          setProgress({
            ...progress,
            phase: 'complete',
            message: 'Import completed while app was in background'
          });
          setIsProcessing(false);
        }
      }
    } catch (error) {
      console.warn('Failed to restore processing state:', error);
    }
  };

  const steps = [
    { id: 1, name: 'File Upload', description: 'Upload CSV file from traffic authority' },
    { id: 2, name: 'File Validation', description: 'Validate file format and structure' },
    { id: 3, name: 'Duplicate Detection', description: 'Check for duplicate fine records' },
    { id: 4, name: 'Vehicle Matching', description: 'Match vehicles using plate numbers' },
    { id: 5, name: 'Fine Processing', description: 'Process and categorize fines with batching' },
    { id: 6, name: 'Database Update', description: 'Save fine data to accounting system' },
  ];

  const pickFile = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: 'text/csv',
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets[0]) {
        setSelectedFile(result.assets[0]);
        setProgress({
          phase: 'idle',
          currentStep: 0,
          totalSteps: 6,
          message: `Selected: ${result.assets[0].name}`,
          sessionId
        });
        setImportStats(null);
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to pick file');
      console.error('File picker error:', error);
    }
  };

  const performOptimizedImport = async (skipDuplicateCheck = false) => {
    if (!selectedFile) {
      Alert.alert('Error', 'Please select a file first');
      return;
    }

    setIsProcessing(true);
    
    try {
      // Step 1: File Upload & Reading
      setProgress({ 
        phase: 'uploading', 
        currentStep: 1, 
        totalSteps: 6, 
        message: 'Reading file...', 
        sessionId,
        details: 'Loading traffic fines CSV content into memory'
      });
      
      let content: string;
      if (selectedFile.uri) {
        const response = await fetch(selectedFile.uri);
        content = await response.text();
      } else {
        content = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.onerror = reject;
          reader.readAsText(selectedFile as any);
        });
      }
      
      // Step 2: File Validation
      setProgress({ 
        phase: 'processing', 
        currentStep: 2, 
        totalSteps: 6, 
        message: 'Validating file format...',
        sessionId,
        details: 'Checking CSV structure and traffic fine headers'
      });

      const lines = content.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        throw new Error('File appears to be empty or invalid');
      }

      const headers = lines[0].split(',');
      console.log('Traffic Fines CSV Headers:', headers);

      // Expected fine headers: Plate Number, Fine Type, Date, Amount, Location, etc.
      const requiredHeaders = ['plate', 'fine', 'date', 'amount'];
      const headerCheck = requiredHeaders.some(required => 
        headers.some(header => header.toLowerCase().includes(required.toLowerCase()))
      );

      if (!headerCheck) {
        throw new Error('Invalid traffic fines CSV format. Required columns: Plate Number, Fine Type, Date, Amount');
      }

      // Step 3: Duplicate Detection (if not skipped)
      if (!skipDuplicateCheck) {
        setProgress({ 
          phase: 'processing', 
          currentStep: 3, 
          totalSteps: 6, 
          message: 'Checking for duplicates...',
          sessionId,
          details: 'Verifying against existing fine records'
        });

        const duplicateResult = await checkForDuplicatesOptimized(lines);
        if (duplicateResult.hasDuplicates) {
          setIsProcessing(false);
          
          if (duplicateResult.isDuplicateFile) {
            Alert.alert(
              'Duplicate File Detected',
              `This traffic fines file was already imported. Records: ${duplicateResult.duplicateCount}`,
              [
                { text: 'Cancel', style: 'cancel' },
                { text: 'Force Re-import', onPress: () => performOptimizedImport(true) }
              ]
            );
            return;
          } else {
            Alert.alert(
              'Potential Duplicates',
              `Found ${duplicateResult.duplicateCount} potential duplicate fine records.`,
              [
                { text: 'Cancel', style: 'cancel' },
                { text: 'Continue', onPress: () => processDataChunked(lines) }
              ]
            );
            return;
          }
        }
      }

      await processDataChunked(lines);

    } catch (error) {
      console.error('Traffic fines import error:', error);
      setProgress({
        phase: 'error',
        currentStep: 0,
        totalSteps: 6,
        message: 'Import failed',
        sessionId,
        details: error.message || 'An unexpected error occurred'
      });
      setIsProcessing(false);
    }
  };

  const checkForDuplicatesOptimized = async (lines: string[]) => {
    try {
      const dateRange = extractDateRangeFromFilename(selectedFile?.name || '');
      const fileHash = await calculateFileHash(lines.join('\n'));
      
      // Quick hash-based duplicate check
      const { data: hashCheck } = await supabase
        .from('accounting_transactions')
        .select('id, import_batch_id')
        .eq('category', 'fines')
        .eq('source_filename', selectedFile?.name || '')
        .limit(1);

      if (hashCheck && hashCheck.length > 0) {
        return {
          hasDuplicates: true,
          isDuplicateFile: true,
          duplicateCount: lines.length - 1
        };
      }

      // Basic date range check for traffic fines
      const { data: dateCheck } = await supabase
        .from('accounting_transactions')
        .select('id', { count: 'exact', head: true })
        .eq('category', 'fines')
        .gte('transaction_date', dateRange.startDate)
        .lte('transaction_date', dateRange.endDate);

      return {
        hasDuplicates: (dateCheck || 0) > 0,
        isDuplicateFile: false,
        duplicateCount: dateCheck || 0
      };
    } catch (error) {
      console.warn('Duplicate check failed:', error);
      return { hasDuplicates: false, duplicateCount: 0 };
    }
  };

  const processDataChunked = async (lines: string[]) => {
    // Step 4: Vehicle Matching
    setProgress({ 
      phase: 'processing', 
      currentStep: 4, 
      totalSteps: 6, 
      message: 'Matching vehicles...',
      sessionId,
      details: 'Finding vehicles by plate numbers in database'
    });

    const batchId = crypto.randomUUID();
    const fileHash = await calculateFileHash(lines.join('\n'));
    const dateRange = extractDateRangeFromFilename(selectedFile?.name || '');
    
    let processedCount = 0;
    let totalAmount = 0;
    let vehicleMatches = 0;
    let plateMatches = 0;
    let notFoundCount = 0;
    let duplicatesRemoved = 0;
    
    // Fine categorization counters
    const fineCategories = {
      parking: 0,
      speeding: 0,
      traffic: 0,
      other: 0
    };

    const chunkSize = 25; // Smaller chunks for fines due to complexity
    const dataLines = lines.slice(1); // Skip header
    const totalRecords = dataLines.length;

    // Step 5: Chunked Data Processing
    setProgress({ 
      phase: 'processing', 
      currentStep: 5, 
      totalSteps: 6, 
      message: 'Processing fine data in chunks...',
      sessionId,
      details: `Processing ${totalRecords} fine records in batches of ${chunkSize}`,
      processed: 0,
      total: totalRecords
    });

    for (let i = 0; i < dataLines.length; i += chunkSize) {
      const chunk = dataLines.slice(i, i + chunkSize);
      const chunkResults = await processChunk(chunk, batchId, fileHash, dateRange, i, fineCategories);
      
      processedCount += chunkResults.processed;
      totalAmount += chunkResults.totalAmount;
      vehicleMatches += chunkResults.vehicleMatches;
      plateMatches += chunkResults.plateMatches;
      notFoundCount += chunkResults.notFound;
      duplicatesRemoved += chunkResults.duplicatesRemoved;

      // Update progress
      setProgress({ 
        phase: 'processing', 
        currentStep: 5, 
        totalSteps: 6, 
        message: 'Processing fine data in chunks...',
        sessionId,
        details: `Processed ${Math.min(i + chunkSize, totalRecords)} of ${totalRecords} fine records`,
        processed: Math.min(i + chunkSize, totalRecords),
        total: totalRecords
      });

      // Save state after each chunk
      await saveSessionState();

      // Small delay to prevent UI blocking
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    // Step 6: Final Database Update
    setProgress({ 
      phase: 'processing', 
      currentStep: 6, 
      totalSteps: 6, 
      message: 'Finalizing import...',
      sessionId,
      details: 'Saving final results and categorization'
    });

    const stats: ImportStats = {
      totalRecords,
      vehicleMatches: vehicleMatches,
      plateMatches: plateMatches,
      notFound: notFoundCount,
      duplicatesRemoved,
      totalAmount,
      accuracy: Math.round(((vehicleMatches + plateMatches) / totalRecords) * 100),
      dateRange: `${dateRange.startDate} to ${dateRange.endDate}`,
      batchId: batchId.substring(0, 8),
      sourceFile: selectedFile?.name || 'unknown',
      importType: 'Chunked Processing',
      fileHash: fileHash.substring(0, 16),
      processedRecords: processedCount,
      rawRecordsSaved: totalRecords,
      categorizedFines: fineCategories
    };

    // Create company transaction for fine payment
    if (processedCount > 0 && totalAmount > 0) {
      console.log('💰 Creating company transaction for fines payment...');
      
      const { error: companyTxError } = await supabase
        .from('company_cash_transactions')
        .insert({
          transaction_date: new Date().toISOString().split('T')[0],
          transaction_type: 'bank_withdrawal',
          category: 'fines_payment',
          amount: totalAmount,
          payment_method: 'bank_transfer',
          reference_number: batchId,
          related_batch_id: batchId,
          verified: false,
          notes: `Traffic fines payment - ${processedCount} fines imported from ${selectedFile?.name || 'file'}`,
          created_by: null // Will be set by RLS policy
        });

      if (companyTxError) {
        console.error('⚠️ Failed to create company transaction:', companyTxError.message);
      } else {
        console.log('✅ Company transaction created successfully');
      }

      // Create reconciliation record
      const { error: reconError } = await supabase
        .from('batch_reconciliation')
        .insert({
          batch_id: batchId,
          import_type: 'fines',
          import_date: new Date().toISOString(),
          source_file_name: selectedFile?.name || 'unknown',
          total_amount_calculated: totalAmount,
          total_amount_from_file: totalAmount,
          difference_amount: 0,
          record_count: processedCount,
          reconciliation_status: 'matched',
          notes: `Traffic fines import - ${processedCount} fines, ${duplicatesRemoved} duplicates skipped`,
          reconciled_by: null // Will be set by RLS policy
        });

      if (reconError) {
        console.error('⚠️ Failed to create reconciliation record:', reconError.message);
      } else {
        console.log('✅ Reconciliation record created');
      }
    }

    setImportStats(stats);
    setProgress({ 
      phase: 'complete', 
      currentStep: 6, 
      totalSteps: 6, 
      message: 'Traffic fines import completed successfully!',
      sessionId,
      details: `${vehicleMatches + plateMatches} fine transactions imported for period ${stats.dateRange}`
    });
    
    setIsProcessing(false);
    
    // Clear session state
    await storage.removeItem(`fines_import_${sessionId}`);
  };

  const processChunk = async (chunk: string[], batchId: string, fileHash: string, dateRange: any, startIndex: number, fineCategories: any) => {
    const transactions: any[] = [];
    let processed = 0;
    let totalAmount = 0;
    let vehicleMatches = 0;
    let plateMatches = 0;
    let notFound = 0;
    let duplicatesRemoved = 0;

    for (let i = 0; i < chunk.length; i++) {
      const line = chunk[i].trim();
      if (!line) continue;

      const cells = line.split(',');
      
      // Parse Fine XLSX format - Excel columns from traffic fines file
      // Columns: ['Unnamed: 0', 'Fine No.', 'Date', 'Fine Place', 'Plate No.', 'Total Amount', 'Discount (%)', 'Amount after Discount']
      const fineNumber = cells[1]?.trim() || '';        // Column 1: Fine No.
      const fineDate = cells[2]?.trim() || '';          // Column 2: Date
      const fineLocation = cells[3]?.trim() || '';      // Column 3: Fine Place
      const plateNumber = cells[4]?.trim() || '';       // Column 4: Plate No. (THIS IS THE FIX!)
      const totalAmount = parseFloat(cells[5]?.trim() || '0');  // Column 5: Total Amount
      const discountPercent = parseFloat(cells[6]?.trim() || '0'); // Column 6: Discount (%)
      const fineAmount = parseFloat(cells[7]?.trim() || '0');   // Column 7: Amount after Discount
      
      // Use fine location as type/description since original file doesn't have separate "type" field
      const fineType = fineLocation; 
      const fineDescription = `Fine #${fineNumber} at ${fineLocation}`;
      
      if (fineAmount > 0 && plateNumber !== '') {
        // Find vehicle by plate number - ONLY import fines for vehicles we own
        const result = await findVehicleByPlateOptimized(plateNumber);
        
        // Skip this fine if vehicle is not in our system (belongs to other companies)
        if (!result.vehicle) {
          notFound++;
          continue; // Move to next fine without processing this one
        }
        
        // Parse date
        const transactionDate = parseFineDate(fineDate);
        
        // Categorize fine
        const category = categorizeFine(fineType, fineDescription);
        fineCategories[category]++;
        
        // Track match type
        if (result.matchType === 'exact') vehicleMatches++;
        else if (result.matchType === 'partial') plateMatches++;
        
        // Check for duplicates within this import
        const duplicateKey = `${plateNumber}_${transactionDate}_${fineAmount}_${fineNumber}`;
        if (!processedTransactions.has(duplicateKey)) {
          transactions.push({
            transaction_date: transactionDate,
            type: 'expense',
            category: 'fines',
            amount: fineAmount,
            description: `${category.toUpperCase()} Fine: ${fineDescription} - Plate ${plateNumber}`,
            vehicle_id: result.vehicle.id,
            driver_id: result.vehicle.assignedDriverId || null,
            import_batch_id: batchId,
            batch_start_date: dateRange.startDate,
            batch_end_date: dateRange.endDate,
            source_filename: selectedFile?.name || 'unknown_file',
            session_id: sessionId,
            metadata: JSON.stringify({
              fine_number: fineNumber,
              fine_type: fineType,
              plate_number: plateNumber,
              location: fineLocation,
              description: fineDescription,
              category: category,
              original_amount: totalAmount,
              discount_percent: discountPercent
            })
          });

          processedTransactions.add(duplicateKey);
          totalAmount += fineAmount;
          processed++;
        } else {
          duplicatesRemoved++;
        }
      }
    }

    // Batch insert transactions
    if (transactions.length > 0) {
      const { error: transError } = await supabase
        .from('accounting_transactions')
        .insert(transactions);
      
      if (transError) {
        console.warn('Failed to insert fine transactions:', transError);
      }
    }

    return {
      processed,
      totalAmount,
      vehicleMatches,
      plateMatches,
      notFound,
      duplicatesRemoved
    };
  };

  // Track processed transactions to avoid duplicates within same import
  const processedTransactions = useRef(new Set());

  // Optimized vehicle lookup with caching
  const vehicleCache = useRef(new Map());
  
  const findVehicleByPlateOptimized = async (plateNumber: string) => {
    const cacheKey = plateNumber.toUpperCase();
    
    if (vehicleCache.current.has(cacheKey)) {
      return vehicleCache.current.get(cacheKey);
    }

    try {
      let result = { vehicle: null, matchType: 'none' };

      // Try exact plate match
      if (plateNumber && plateNumber.trim() !== '') {
        const { data: vehicleExact } = await supabase
          .from('vehicles')
          .select('id, license_plate, assignedDriverId')
          .eq('license_plate', plateNumber.trim().toUpperCase())
          .limit(1);
        
        if (vehicleExact && vehicleExact.length > 0) {
          result = { vehicle: vehicleExact[0], matchType: 'exact' };
        } else {
          // Try partial match (remove spaces, special characters)
          const cleanPlate = plateNumber.replace(/[^A-Z0-9]/g, '');
          const { data: vehiclePartial } = await supabase
            .from('vehicles')
            .select('id, license_plate, assignedDriverId')
            .ilike('license_plate', `%${cleanPlate}%`)
            .limit(1);
          
          if (vehiclePartial && vehiclePartial.length > 0) {
            result = { vehicle: vehiclePartial[0], matchType: 'partial' };
          }
        }
      }

      // Cache the result
      vehicleCache.current.set(cacheKey, result);
      return result;
    } catch (error) {
      console.warn(`Error finding vehicle ${plateNumber}:`, error);
      const result = { vehicle: null, matchType: 'error' };
      vehicleCache.current.set(cacheKey, result);
      return result;
    }
  };

  // Helper functions
  const calculateFileHash = async (content: string): Promise<string> => {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(content);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      return 'hash_failed_' + Date.now();
    }
  };

  const extractDateRangeFromFilename = (filename: string) => {
    const datePattern = /(\d{4}-\d{2}-\d{2})/g;
    const dates = filename.match(datePattern);
    
    if (dates && dates.length >= 2) {
      return { startDate: dates[0], endDate: dates[dates.length - 1] };
    }
    
    // Default to current month for fines
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    return {
      startDate: startOfMonth.toISOString().split('T')[0],
      endDate: endOfMonth.toISOString().split('T')[0]
    };
  };

  const parseFineDate = (date: string) => {
    try {
      // Parse fine date format (adjust as needed)
      const parsedDate = new Date(date);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toISOString().split('T')[0];
      }
    } catch (error) {
      console.warn('Date parsing error:', error);
    }
    
    // Fallback to current date
    return new Date().toISOString().split('T')[0];
  };

  const categorizeFine = (fineType: string, description: string): 'parking' | 'speeding' | 'traffic' | 'other' => {
    const combined = `${fineType} ${description}`.toLowerCase();
    
    if (combined.includes('park') || combined.includes('zone') || combined.includes('meter')) {
      return 'parking';
    } else if (combined.includes('speed') || combined.includes('exceed') || combined.includes('limit')) {
      return 'speeding';
    } else if (combined.includes('signal') || combined.includes('light') || combined.includes('stop') || 
               combined.includes('lane') || combined.includes('turn') || combined.includes('overtake')) {
      return 'traffic';
    } else {
      return 'other';
    }
  };

  const resetImport = () => {
    setSelectedFile(null);
    setProgress({
      phase: 'idle',
      currentStep: 0,
      totalSteps: 6,
      message: 'Ready to import traffic fines data',
    });
    setImportStats(null);
    setIsProcessing(false);
    vehicleCache.current.clear();
    processedTransactions.current.clear();
  };

  const getStepStatus = (stepNumber: number) => {
    if (progress.currentStep > stepNumber) return 'complete';
    if (progress.currentStep === stepNumber && isProcessing) return 'active';
    return 'pending';
  };

  const getStepColor = (status: string) => {
    switch (status) {
      case 'complete': return theme.colors.success;
      case 'active': return '#DC2626'; // Red for fines/violations
      default: return theme.colors.gray400;
    }
  };

  const StepIndicator = ({ step, status }: { step: any; status: string }) => (
    <View style={styles.stepItem}>
      <View style={[styles.stepCircle, { borderColor: getStepColor(status) }]}>
        {status === 'complete' ? (
          <Ionicons name="checkmark" size={16} color={theme.colors.success} />
        ) : status === 'active' ? (
          <ActivityIndicator size="small" color="#DC2626" />
        ) : (
          <Text style={[styles.stepNumber, { color: getStepColor(status) }]}>{step.id}</Text>
        )}
      </View>
      <View style={styles.stepInfo}>
        <Text style={[styles.stepName, { color: getStepColor(status) }]}>{step.name}</Text>
        <Text style={styles.stepDescription}>{step.description}</Text>
      </View>
    </View>
  );

  return (
    <AppLayout title="Fines Import (Fixed)" showBackButton onBackPress={() => navigation.goBack()}>
      <SafeAreaView style={styles.container}>
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
          {/* Header */}
          <View style={styles.header}>
            <View style={[styles.iconContainer, { backgroundColor: '#DC2626' + '15' }]}>
              <Ionicons name="warning" size={24} color="#DC2626" />
            </View>
            <View style={styles.headerInfo}>
              <Text style={styles.title}>Traffic Fines Import (Optimized)</Text>
              <Text style={styles.subtitle}>Fixed background processing & fine categorization</Text>
            </View>
          </View>

          {/* Progress Indicator */}
          {isProcessing && (
            <View style={styles.backgroundWarning}>
              <Ionicons name="shield-checkmark" size={20} color="#DC2626" />
              <Text style={styles.backgroundText}>
                Safe to minimize app - fine import will continue in background
              </Text>
            </View>
          )}

          {/* File Selection */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>1. Select File</Text>
            <TouchableOpacity 
              style={[styles.filePickerButton, { borderColor: '#DC2626' }]} 
              onPress={pickFile}
              disabled={isProcessing}
            >
              <Ionicons name="cloud-upload" size={20} color="#DC2626" />
              <Text style={[styles.filePickerText, { color: '#DC2626' }]}>
                {selectedFile ? selectedFile.name : 'Choose Traffic Fines CSV file'}
              </Text>
            </TouchableOpacity>
            {selectedFile && (
              <View style={styles.fileInfo}>
                <Ionicons name="document-text" size={16} color={theme.colors.success} />
                <Text style={styles.fileInfoText}>
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                </Text>
              </View>
            )}
          </View>

          {/* Progress Steps */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>2. Import Progress</Text>
            <View style={styles.stepsContainer}>
              {steps.map((step) => (
                <StepIndicator 
                  key={step.id} 
                  step={step} 
                  status={getStepStatus(step.id)} 
                />
              ))}
            </View>

            {progress.phase !== 'idle' && (
              <View style={[styles.progressCard, { borderLeftColor: '#DC2626' }]}>
                <View style={styles.progressHeader}>
                  <Text style={styles.progressMessage}>{progress.message}</Text>
                  {isProcessing && (
                    <ActivityIndicator size="small" color="#DC2626" />
                  )}
                </View>
                {progress.details && (
                  <Text style={styles.progressDetails}>{progress.details}</Text>
                )}
                {progress.processed && progress.total && (
                  <View style={styles.progressContainer}>
                    <View style={styles.progressBar}>
                      <View 
                        style={[
                          styles.progressFill, 
                          { 
                            width: `${(progress.processed / progress.total) * 100}%`,
                            backgroundColor: '#DC2626'
                          }
                        ]} 
                      />
                    </View>
                    <Text style={styles.progressText}>
                      {progress.processed}/{progress.total} ({Math.round((progress.processed / progress.total) * 100)}%)
                    </Text>
                  </View>
                )}
              </View>
            )}
          </View>

          {/* Import Results */}
          {importStats && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>3. Import Results</Text>
              <View style={styles.statsContainer}>
                <View style={styles.statCard}>
                  <Text style={styles.statNumber}>{importStats.totalRecords}</Text>
                  <Text style={styles.statLabel}>Total Records</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: '#DC2626' }]}>
                    {importStats.vehicleMatches}
                  </Text>
                  <Text style={styles.statLabel}>Vehicle Matches</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: theme.colors.warning }]}>
                    {importStats.plateMatches}
                  </Text>
                  <Text style={styles.statLabel}>Plate Matches</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: theme.colors.error }]}>
                    {importStats.notFound}
                  </Text>
                  <Text style={styles.statLabel}>Not Found</Text>
                </View>
              </View>

              {/* Fine Categories */}
              {importStats.categorizedFines && (
                <View style={styles.categoriesContainer}>
                  <Text style={styles.categoriesTitle}>Fine Categories</Text>
                  <View style={styles.categoriesGrid}>
                    <View style={styles.categoryCard}>
                      <Text style={[styles.categoryNumber, { color: '#DC2626' }]}>
                        {importStats.categorizedFines.parking}
                      </Text>
                      <Text style={styles.categoryLabel}>Parking</Text>
                    </View>
                    <View style={styles.categoryCard}>
                      <Text style={[styles.categoryNumber, { color: '#DC2626' }]}>
                        {importStats.categorizedFines.speeding}
                      </Text>
                      <Text style={styles.categoryLabel}>Speeding</Text>
                    </View>
                    <View style={styles.categoryCard}>
                      <Text style={[styles.categoryNumber, { color: '#DC2626' }]}>
                        {importStats.categorizedFines.traffic}
                      </Text>
                      <Text style={styles.categoryLabel}>Traffic</Text>
                    </View>
                    <View style={styles.categoryCard}>
                      <Text style={[styles.categoryNumber, { color: '#DC2626' }]}>
                        {importStats.categorizedFines.other}
                      </Text>
                      <Text style={styles.categoryLabel}>Other</Text>
                    </View>
                  </View>
                </View>
              )}

              <View style={styles.summaryCard}>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Total Fine Amount:</Text>
                  <Text style={[styles.summaryValue, { color: '#DC2626' }]}>
                    AED {importStats.totalAmount.toLocaleString()}
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Accuracy:</Text>
                  <Text style={[styles.summaryValue, { color: '#DC2626' }]}>
                    {importStats.accuracy}%
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Duplicates Removed:</Text>
                  <Text style={styles.summaryValue}>
                    {importStats.duplicatesRemoved}
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Batch ID:</Text>
                  <Text style={styles.summaryValue}>
                    {importStats.batchId}
                  </Text>
                </View>
              </View>
            </View>
          )}

          {/* Action Buttons */}
          <View style={styles.actionContainer}>
            {!isProcessing ? (
              <>
                <TouchableOpacity 
                  style={[
                    styles.primaryButton, 
                    { backgroundColor: '#DC2626' },
                    !selectedFile && styles.disabledButton
                  ]} 
                  onPress={() => performOptimizedImport(false)}
                  disabled={!selectedFile}
                >
                  <Ionicons name="cloud-upload" size={20} color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>
                    Import with Duplicate Check
                  </Text>
                </TouchableOpacity>
                
                <TouchableOpacity 
                  style={[
                    styles.primaryButton, 
                    { backgroundColor: '#FF8800', marginTop: 8 },
                    !selectedFile && styles.disabledButton
                  ]} 
                  onPress={() => performOptimizedImport(true)}
                  disabled={!selectedFile}
                >
                  <Ionicons name="flash" size={20} color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>Quick Import (Skip Duplicates)</Text>
                </TouchableOpacity>
                
                {progress.phase === 'complete' && (
                  <TouchableOpacity 
                    style={[styles.secondaryButton, { borderColor: '#DC2626', marginTop: 8 }]} 
                    onPress={resetImport}
                  >
                    <Ionicons name="refresh" size={20} color="#DC2626" />
                    <Text style={[styles.secondaryButtonText, { color: '#DC2626' }]}>Reset</Text>
                  </TouchableOpacity>
                )}
              </>
            ) : (
              <View style={styles.processingContainer}>
                <TouchableOpacity style={styles.disabledButton} disabled>
                  <ActivityIndicator size="small" color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>Processing... (Safe to minimize)</Text>
                </TouchableOpacity>
                <Text style={styles.processingNote}>
                  Fine import will continue in background. You can safely switch to other apps.
                </Text>
              </View>
            )}
          </View>
        </ScrollView>
      </SafeAreaView>
    </AppLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  scrollView: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: theme.borderRadius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  headerInfo: {
    flex: 1,
  },
  title: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  backgroundWarning: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: '#DC2626' + '10',
    borderWidth: 1,
    borderColor: '#DC2626' + '30',
    margin: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing.sm,
  },
  backgroundText: {
    fontSize: theme.typography.sizes.sm,
    color: '#DC2626',
    fontWeight: '500' as any,
  },
  section: {
    backgroundColor: theme.colors.white,
    marginTop: theme.spacing.md,
    padding: theme.spacing.lg,
  },
  sectionTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  filePickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.lg,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: theme.borderRadius.lg,
    backgroundColor: '#DC2626' + '05',
    gap: theme.spacing.sm,
  },
  filePickerText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
  },
  fileInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing.sm,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.success + '10',
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing.xs,
  },
  fileInfoText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.success,
  },
  stepsContainer: {
    gap: theme.spacing.md,
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  stepNumber: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as any,
  },
  stepInfo: {
    flex: 1,
  },
  stepName: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    marginBottom: 2,
  },
  stepDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  progressCard: {
    marginTop: theme.spacing.lg,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    borderLeftWidth: 4,
  },
  progressHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.xs,
  },
  progressMessage: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
  },
  progressDetails: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.sm,
  },
  progressContainer: {
    gap: theme.spacing.xs,
  },
  progressBar: {
    height: 6,
    backgroundColor: theme.colors.gray200,
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
  },
  progressText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  statsContainer: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.lg,
  },
  statCard: {
    flex: 1,
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
  },
  statNumber: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  categoriesContainer: {
    marginBottom: theme.spacing.lg,
  },
  categoriesTitle: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  categoriesGrid: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
  },
  categoryCard: {
    flex: 1,
    alignItems: 'center',
    padding: theme.spacing.sm,
    backgroundColor: '#DC2626' + '10',
    borderRadius: theme.borderRadius.md,
  },
  categoryNumber: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '700' as any,
    marginBottom: 2,
  },
  categoryLabel: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  summaryCard: {
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  summaryValue: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  actionContainer: {
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  primaryButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.white,
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.white,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    gap: theme.spacing.sm,
  },
  secondaryButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
  },
  disabledButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.gray400,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  processingContainer: {
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  processingNote: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    fontStyle: 'italic',
  },
});

export default FineImportScreenFixed;
